import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Briefcase, CheckCircle, Clock, AlertTriangle, Activity } from 'lucide-react';
import { Link } from 'react-router-dom';

const EmployeeOverview = () => {
  const user = JSON.parse(localStorage.getItem('portalUser')) || {};
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const token = localStorage.getItem('portalToken') || localStorage.getItem('admin');
        const res = await axios.get(window.API_URL + '/api/portal/shared/tasks', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTasks(res.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchTasks();
  }, []);

  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.status === 'Completed').length;
  const pendingTasks = tasks.filter(t => t.status === 'Pending' || t.status === 'In Progress' || t.status === 'Submitted' || t.status === 'Rejected').length;
  const overdueTasks = tasks.filter(t => new Date(t.deadline) < new Date() && t.status !== 'Completed').length;
  const productivity = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-[#f06600]/20 to-orange-50 rounded-2xl p-8 border border-orange-100">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome back, {user.fullName}!</h2>
        <p className="text-gray-600">Here's your employee performance dashboard.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Link to="/dashboard/employee/tasks"><StatCard icon={<Briefcase className="w-6 h-6 text-blue-500" />} title="Total Tasks" value={totalTasks} /></Link>
        <Link to="/dashboard/employee/tasks"><StatCard icon={<CheckCircle className="w-6 h-6 text-green-500" />} title="Completed" value={completedTasks} /></Link>
        <Link to="/dashboard/employee/tasks"><StatCard icon={<Clock className="w-6 h-6 text-yellow-500" />} title="Pending Active" value={pendingTasks} /></Link>
        <Link to="/dashboard/employee/tasks"><StatCard icon={<AlertTriangle className="w-6 h-6 text-red-500" />} title="Overdue" value={overdueTasks} /></Link>
        <Link to="/dashboard/employee/tasks"><StatCard icon={<Activity className="w-6 h-6 text-[#f06600]" />} title="Productivity" value={`${productivity}%`} /></Link>
      </div>

      <div className="bg-white shadow-sm rounded-xl p-6 border border-gray-200 mt-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-900">Recent Task Activity</h3>
          <Link to="/dashboard/employee/tasks" className="text-sm font-medium text-[#f06600] hover:text-[#d05800] transition-colors">
            View & Manage All Tasks &rarr;
          </Link>
        </div>
        <div className="space-y-4">
          {tasks.slice(0, 5).map(task => (
            <Link key={task._id} to="/dashboard/employee/tasks" className="flex justify-between items-center p-4 bg-gray-50 rounded-lg border border-gray-100 hover:border-[#f06600]/30 transition-colors block">
              <div>
                <p className="font-semibold text-gray-900">{task.title}</p>
                <p className="text-xs text-gray-500">Deadline: {new Date(task.deadline).toLocaleDateString()}</p>
              </div>
              <span className={`px-2 py-1 rounded text-xs font-bold ${
                task.status === 'Completed' ? 'bg-green-100 text-green-600' :
                task.status === 'Submitted' ? 'bg-blue-100 text-blue-600' :
                task.status === 'In Progress' ? 'bg-orange-100 text-orange-600' :
                task.status === 'Pending' ? 'bg-yellow-100 text-yellow-600' : 'bg-red-100 text-red-600'
              }`}>{task.status}</span>
            </Link>
          ))}
          {tasks.length === 0 && <p className="text-gray-500 text-sm">No recent activity.</p>}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, title, value }) => (
  <div className="bg-white shadow-sm rounded-xl p-4 border border-gray-200 flex flex-col items-center justify-center text-center hover:border-[#f06600]/30 transition-colors">
    <div className="p-2 bg-gray-50 rounded-full mb-2">{icon}</div>
    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">{title}</p>
    <h4 className="text-2xl font-bold text-gray-900">{value}</h4>
  </div>
);

export default EmployeeOverview;
